import React from 'react';
import themesConfig from '@/config/themes.json';

type SectionName = 'hero' | 'metrics' | 'pricing' | 'features' | 'calculator' | 'compatibility' | 'testimonials' | 'faq';

interface BackgroundDecorProps {
  section?: SectionName;
  seed?: number;
}

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

export default function BackgroundDecor({ section = 'pricing', seed }: BackgroundDecorProps) {
  const decorConfig = themesConfig.backgroundDecor[section] || themesConfig.backgroundDecor.pricing;
  const sizes = themesConfig.sizes;
  const blurLevels = themesConfig.blurLevels;

  const effectiveSeed = seed ?? section.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

  const randomizedDecor = decorConfig.slice(0, 3).map((shape: any, index: number) => {
    const shapeSeed = effectiveSeed + index * 13.37;
    const offsetX = (seededRandom(shapeSeed) - 0.5) * 20;
    const offsetY = (seededRandom(shapeSeed + 1.2) - 0.5) * 20;
    const offsetOpacity = (seededRandom(shapeSeed + 2.4) - 0.5) * 10;

    const newShape = JSON.parse(JSON.stringify(shape));

    const applyOffset = (val: any, offset: number) => {
      if (typeof val === 'string' && val.endsWith('%')) {
        const num = parseFloat(val);
        return `${(num + offset).toFixed(3)}%`;
      }
      if (typeof val === 'number') {
        return Number((val + offset).toFixed(3));
      }
      return val;
    };

    if (newShape.position.top !== undefined) newShape.position.top = applyOffset(newShape.position.top, offsetY);
    if (newShape.position.bottom !== undefined) newShape.position.bottom = applyOffset(newShape.position.bottom, offsetY);
    if (newShape.position.left !== undefined) newShape.position.left = applyOffset(newShape.position.left, offsetX);
    if (newShape.position.right !== undefined) newShape.position.right = applyOffset(newShape.position.right, offsetX);
    newShape.opacity = Number(Math.max(5, Math.min(40, newShape.opacity + offsetOpacity)).toFixed(3));

    return newShape;
  });

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {randomizedDecor.map((shape: any, index: number) => {
        const size = sizes[shape.size as keyof typeof sizes];
        const blur = blurLevels[shape.blur as keyof typeof blurLevels];

        const colorMap: Record<string, string> = {
          primary: 'var(--primary)',
          secondary: 'var(--secondary)',
          tertiary: 'var(--tertiary)',
        };

        const bgColor = colorMap[shape.color] || colorMap.primary;

        const positionStyles: React.CSSProperties = {};
        if (shape.position.top !== undefined) positionStyles.top = shape.position.top;
        if (shape.position.bottom !== undefined) positionStyles.bottom = shape.position.bottom;
        if (shape.position.left !== undefined) positionStyles.left = shape.position.left;
        if (shape.position.right !== undefined) positionStyles.right = shape.position.right;
        if (shape.position.transform !== undefined) positionStyles.transform = shape.position.transform;

        return (
          <div
            key={`${section}-${index}`}
            className="absolute pointer-events-none rounded-full"
            style={{
              width: size.width,
              height: size.height,
              backgroundColor: `hsl(${bgColor})`,
              opacity: (shape.opacity / 100) * 0.5,
              filter: `blur(${blur})`,
              ...positionStyles,
            }}
          />
        );
      })}
    </div>
  );
}
